import type { Guest } from '~/@entities/Guest'
import {
  adaptAssignedWorksheetProblemDetails,
  type AssignedWorksheetProblemDetail,
} from '~/@entities/StudentWorksheet'
import type {
  AssignData,
  CopyData,
  Create2Data,
  CreverseEntranceWorksheetAssignmentCancellationRequest,
  CreverseEntranceWorksheetAssignRequest,
  CreverseEntranceWorksheetBulkDeleteRequest,
  CreverseEntranceWorksheetCopyRequest,
  CreverseEntranceWorksheetCreateRequest,
  CreverseEntranceWorksheetRestoreRequest,
  CreverseEntranceWorksheetUpdatePartRequest,
  CreverseEntranceWorksheetUpdateRequest,
  GetAssignedWorksheetsData,
  GetAssignedWorksheetsParams,
  GetEntranceWorksheetsByParams,
  GetScoringProblemsData,
  GetScoringProblemsParams,
  ScoreData,
  ScorePayload,
  SliceCreverseEntranceWorksheetResponse,
} from '~/api-spec/__generated__/main'
import { httpClient, type HttpFile, type RequestConfig, type RequestMeta } from '~/http/client'
import type { WithDefaultedOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptEntranceWorksheetAssignEcho,
  adaptEntranceWorksheetCreation,
  adaptEntranceWorksheets,
  adaptGuestEntranceScoringResult,
  adaptGuestEntranceWorksheets,
} from './adapters'
import type {
  EntranceWorksheet,
  EntranceWorksheetAssignEcho,
  EntranceWorksheetCreation,
  GuestEntranceScoringResult,
  GuestEntranceWorksheet,
} from './schemas'

export type EntranceWorksheetListParams = GetEntranceWorksheetsByParams
export type EntranceWorksheetRestoreBody = CreverseEntranceWorksheetRestoreRequest
export type EntranceWorksheetDeleteBody = CreverseEntranceWorksheetBulkDeleteRequest
// 생성·수정 요청의 problemList·qrFlag·problemTrendFlag·problemPadding(생성은 assignedTargets 도)은 서버 Kotlin
// 생성자 기본값이 있어 생략할 수 있다. 2026-10-13 백엔드 develop(cc5f60e17) creverseEntrance/controller/request/
// CreverseEntranceWorksheet{Create,Update,UpdatePart}Request.kt 로 확인.
type DefaultedPrintOption = 'qrFlag' | 'problemTrendFlag' | 'problemPadding'
export type EntranceWorksheetCreateBody = WithDefaultedOptionals<
  CreverseEntranceWorksheetCreateRequest,
  DefaultedPrintOption | 'problemList' | 'assignedTargets'
>
export type EntranceWorksheetUpdateBody = WithDefaultedOptionals<
  CreverseEntranceWorksheetUpdateRequest,
  DefaultedPrintOption | 'problemList'
>
export type EntranceWorksheetPartUpdateBody = WithDefaultedOptionals<
  CreverseEntranceWorksheetUpdatePartRequest,
  DefaultedPrintOption
>
export type EntranceWorksheetCopyBody = CreverseEntranceWorksheetCopyRequest
export type EntranceWorksheetAssignBody = CreverseEntranceWorksheetAssignRequest
export type EntranceWorksheetAssignmentCancelBody =
  CreverseEntranceWorksheetAssignmentCancellationRequest
export type GuestEntranceWorksheetListParams = Omit<GetAssignedWorksheetsParams, 'guestId'>
export type GuestEntranceScoringBody = ScorePayload

// 이 컨트롤러는 CommonResponse 봉투 없이 본문을 바로 낸다.

export const getEntranceWorksheets = async (
  params: EntranceWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<EntranceWorksheet>> => {
  const body = await httpClient().get<SliceCreverseEntranceWorksheetResponse>(
    '/creverse/entrance-worksheet',
    { params, meta },
  )
  return adaptEntranceWorksheets(body)
}

/** 시험지를 만들고 (비재원생, 회차) 배정까지 한 요청에 처리한다 */
export const createEntranceWorksheet = async (
  body: EntranceWorksheetCreateBody,
  config?: Pick<RequestConfig, 'meta' | 'signal'>,
): Promise<EntranceWorksheetCreation> => {
  const created = await httpClient().post<Create2Data>('/creverse/entrance-worksheet', body, config)
  return adaptEntranceWorksheetCreation(created)
}

/** 배정되지 않은 시험지 전체 수정. 배정된 시험지는 서버가 거부한다 */
export const updateEntranceWorksheet = async (
  worksheetId: EntranceWorksheet['id'],
  body: EntranceWorksheetUpdateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put(`/creverse/entrance-worksheet/${worksheetId}`, body, { meta })
}

/** 배정된 시험지의 인쇄 옵션만 수정 */
export const updateEntranceWorksheetPart = async (
  worksheetId: EntranceWorksheet['id'],
  body: EntranceWorksheetPartUpdateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch(`/creverse/entrance-worksheet/${worksheetId}`, body, { meta })
}

/** 휴지통으로 옮긴다 */
export const deleteEntranceWorksheets = async (
  body: EntranceWorksheetDeleteBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/creverse/entrance-worksheet', { data: body, meta })
}

/** 센터간 공유 시험지를 휴지통을 거치지 않고 지운다 */
export const forceDeleteEntranceWorksheets = async (
  body: EntranceWorksheetDeleteBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/creverse/entrance-worksheet/force-delete', { data: body, meta })
}

/** 시험지를 복제해 다른 공유 범위로 만든다. 원본은 남는다 */
export const copyEntranceWorksheet = async (
  worksheetId: EntranceWorksheet['id'],
  body: EntranceWorksheetCopyBody,
  meta?: RequestMeta,
): Promise<EntranceWorksheetCreation> => {
  const copied = await httpClient().post<CopyData>(
    `/creverse/entrance-worksheet/${worksheetId}/copy`,
    body,
    { meta },
  )
  return adaptEntranceWorksheetCreation(copied)
}

export const assignEntranceWorksheets = async (
  body: EntranceWorksheetAssignBody,
  meta?: RequestMeta,
): Promise<EntranceWorksheetAssignEcho> => {
  const echo = await httpClient().post<AssignData>(
    '/creverse/entrance-worksheet/assignments',
    body,
    { meta },
  )
  return adaptEntranceWorksheetAssignEcho(echo)
}

/** 무효한 배정이 섞이면 서버가 전체를 되돌린다 */
export const cancelEntranceWorksheetAssignments = async (
  body: EntranceWorksheetAssignmentCancelBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/creverse/entrance-worksheet/assignments', { data: body, meta })
}

export const getGuestEntranceWorksheets = async (
  guestId: Guest['id'],
  params: GuestEntranceWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<GuestEntranceWorksheet>> => {
  const body = await httpClient().get<GetAssignedWorksheetsData>(
    `/creverse/entrance-worksheet/assignments/${guestId}`,
    { params, meta },
  )
  return adaptGuestEntranceWorksheets(body)
}

export type GuestEntranceScoringProblemListParams = Omit<
  GetScoringProblemsParams,
  'guestEntranceWorksheetId'
>

/**
 * 학습지형 입학고사 배정 한 건의 문항과 채점 기록. 학습지 채점 문항(`/student-worksheet/assign/{id}/problem`)과 같은
 * 응답 클래스를 CommonResponse 봉투 없이 낸다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의
 * creverseEntrance/controller/CreverseEntranceWorksheetScoringController.kt 로 확인.
 */
export const getGuestEntranceScoringProblems = async (
  guestEntranceWorksheetId: GuestEntranceWorksheet['id'],
  params: GuestEntranceScoringProblemListParams,
  meta?: RequestMeta,
): Promise<AssignedWorksheetProblemDetail[]> => {
  const slice = await httpClient().get<GetScoringProblemsData>(
    `/creverse/entrance-worksheet/assignments/${guestEntranceWorksheetId}/problem`,
    { params, meta },
  )
  return adaptAssignedWorksheetProblemDetails(slice)
}

/** 문제별 채점을 저장하고 목록 행을 갱신할 결과를 받는다 */
export const scoreGuestEntranceWorksheet = async (
  guestEntranceWorksheetId: GuestEntranceWorksheet['id'],
  body: GuestEntranceScoringBody,
  meta?: RequestMeta,
): Promise<GuestEntranceScoringResult> => {
  const result = await httpClient().patch<ScoreData>(
    `/creverse/entrance-worksheet/assignments/${guestEntranceWorksheetId}/scoring`,
    body,
    { meta },
  )
  return adaptGuestEntranceScoringResult(result)
}

export const restoreEntranceWorksheets = async (
  body: EntranceWorksheetRestoreBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch('/creverse/entrance-worksheet/restore', body, { meta })
}

// 휴지통에서 고른 시험지를 영구 삭제한다
export const deleteEntranceWorksheetsInTrash = async (
  body: EntranceWorksheetDeleteBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/creverse/entrance-worksheet/trash/by-worksheet', {
    data: body,
    meta,
  })
}

// 학원 휴지통 전체를 비운다
export const emptyEntranceWorksheetTrash = async (meta?: RequestMeta): Promise<void> => {
  await httpClient().delete('/creverse/entrance-worksheet/trash/by-academy', { meta })
}

/** 입학고사 원클릭 보고서 PDF */
export const getGuestEntranceWorksheetReportPdf = async (
  guestEntranceWorksheetId: GuestEntranceWorksheet['id'],
  meta?: RequestMeta,
): Promise<HttpFile> =>
  httpClient().getFile(
    `/creverse/entrance-worksheet/assignments/${guestEntranceWorksheetId}/report/download`,
    { meta },
  )
